import dayjs, { Dayjs } from 'dayjs';

/**
 * Formats a duration in minutes to a standard Elapsed Time (ET) string.
 * Rules:
 * - >= 1 day   : "2d, 04:30h"
 * - >= 1 hour  : "13:30h" (e.g. "02:35h")
 * - >= 1 minute: "45m"
 * - 0          : "0m"
 * - No seconds, truncated to last full minute.
 */
export const formatTatDuration = (totalMinutes: number | null | undefined): string => {
    if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes) || totalMinutes < 0) {
        return '--';
    }

    const total = Math.floor(totalMinutes);

    // Case 0: 0 minutes
    if (total === 0) {
        return '0m';
    }

    const days = Math.floor(total / (24 * 60));
    const hours = Math.floor((total % (24 * 60)) / 60);
    const minutes = Math.floor(total % 60);

    // Duration >= 1 day: e.g. "2d, 04:30h"
    if (days >= 1) {
        return `${days}d, ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}h`;
    }

    // Duration >= 1 hour: e.g. "13:30h"
    if (hours >= 1) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}h`;
    }

    // Duration >= 1 minute: e.g. "45m"
    return `${minutes}m`;
};

/**
 * Cleans UTC/Timezone shift (e.g. Z or offset) from raw backend date string to treat as wall-clock time
 */
export const parseDateClean = (d: string | Date | Dayjs | null | undefined): Dayjs | null => {
    if (!d) return null;
    if (dayjs.isDayjs(d)) return d;
    if (d instanceof Date) return dayjs(d);
    if (typeof d === 'string') {
        const clean = d.replace(/Z$/i, '').replace(/[+-]\d{2}:\d{2}$/, '');
        const parsed = dayjs(clean);
        return parsed.isValid() ? parsed : dayjs(d);
    }
    const fallback = dayjs(d);
    return fallback.isValid() ? fallback : null;
};

/**
 * Calculates Elapsed Time (ET) in minutes between Started On and Completed On (or current time if in-progress).
 * Truncates to last full minute without rounding.
 * Returns null if startedOn is not valid or not provided.
 */
export const calculateTatMinutes = (
    startedOn: string | Date | Dayjs | null | undefined,
    completedOn?: string | Date | Dayjs | null | undefined
): number | null => {
    if (!startedOn) return null;

    const startObj = parseDateClean(startedOn);
    if (!startObj || !startObj.isValid()) return null;
    const start = startObj.second(0).millisecond(0);

    const endObj = completedOn ? parseDateClean(completedOn) : dayjs();
    if (!endObj || !endObj.isValid()) return null;
    const end = endObj.second(0).millisecond(0);

    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes < 0) {
        // If task is completed and completedOn is before startedOn, return null (invalid)
        if (completedOn) return null;
        // If task is in-progress and start time is ahead of current clock, elapsed time is 0m
        return 0;
    }
    return diffMinutes;
};

export type DerivedStatusType = 'Pending' | 'In-Progress' | 'Completed' | 'Invalid';

/**
 * Derives the task status based on Started On and Completed On dates for frontend preview.
 * 
 * Rules:
 * - Case 1: Both empty -> Pending
 * - Case 2: Started filled, Completed empty -> In-Progress
 * - Case 3: Both filled -> Completed
 * - Case 4: Started empty, Completed filled -> Invalid
 */
export const deriveStatusFromDates = (
    startedOn: string | Date | Dayjs | null | undefined,
    completedOn: string | Date | Dayjs | null | undefined
): DerivedStatusType => {
    const hasStarted = Boolean(startedOn && parseDateClean(startedOn)?.isValid());
    const hasCompleted = Boolean(completedOn && parseDateClean(completedOn)?.isValid());

    if (!hasStarted && hasCompleted) {
        return 'Invalid';
    }

    if (!hasStarted && !hasCompleted) {
        return 'Pending';
    }

    if (hasStarted && !hasCompleted) {
        return 'In-Progress';
    }

    return 'Completed';
};

/**
 * Recalculates aggregated Stage TAT in minutes/duration for frontend preview.
 * Aggregates completed task TATs or computes span between earliest started and latest completed.
 */
export const recalcStageTat = (tasks: Array<{ startedOn?: string | null; completedOn?: string | null; status?: string }>): string => {
    if (!tasks || tasks.length === 0) return '--';

    let totalMinutes = 0;
    let hasValidTask = false;

    for (const task of tasks) {
        const mins = calculateTatMinutes(task.startedOn, task.completedOn);
        if (mins !== null) {
            totalMinutes += mins;
            hasValidTask = true;
        }
    }

    return hasValidTask ? formatTatDuration(totalMinutes) : '--';
};

/**
 * Recalculates aggregated Project TAT in minutes/duration for frontend preview.
 */
export const recalcProjectTat = (stagesOrTasks: Array<{ startedOn?: string | null; completedOn?: string | null; status?: string }>): string => {
    return recalcStageTat(stagesOrTasks);
};
